import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Car, Hourglass, Handshake, MapPin, Quote } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { PrimaryCTA } from '@/components/PrimaryCTA';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import founderPhoto from '@/assets/jamie.avif';

const manifestoCards = [
  {
    icon: Car,
    title: 'We Build The Engine, You Drive The Car.',
    content: [
      "Unlike those big agencies that just rent you leads through expensive ads, we build a website you fully own. It's an asset that works for you day in, day out.",
      "We implement lightning-fast one-click-to-call technology and high-speed mobile UI, so enquiries come straight to your phone.",
      "But here's the key: you need to respond quickly. Statistics show responding within 5 minutes boosts conversions by up to 400%."
    ],
  },
  {
    icon: Hourglass,
    title: 'SEO is a Marathon, Not a Sprint.',
    content: [
      "Organic SEO takes time. While Google Ads can cost £50 to £100+ per click for emergency plumbing, roofing and building work right across South Wales, our approach is different.",
      "Our SEO, GEO plus AI-tuned strategy takes 3 to 6 months to fully mature.",
      "Once your site ranks locally on Google Maps and Search across South Wales, it generates free, high-margin enquiries for years, without the relentless pay-to-play tax."
    ],
  },
  {
    icon: Handshake,
    title: 'Your Closing Rate is On You.',
    content: [
      "Nearly 90% of homeowners in South Wales check reviews before hiring a local tradesperson.",
      "We guarantee your online presence will look 5-star across your site, Google Business Profile and Social Media (if selected), so you never lose jobs to competitors because of bad reviews.",
      "Our job is to get you looking good, and get you seen. Yours is to close any enquiries that come your way and do a professional 5-star job!"
    ],
  },
];

export default function About() {
  // Set page metadata
  useEffect(() => {
    // Add AboutPage schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About SmartSite Solutions",
      "description": "Meet the team behind SmartSite Solutions. We help plumbers, builders and trades across South Wales get more leads with high-performance websites.",
      "url": "https://smartsite-lead-gen.lovable.app/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "SmartSite Solutions",
        "foundingDate": "2024",
        "foundingLocation": "North Cornelly, South Wales",
        "description": "High-performance websites for South Wales trades"
      }
    };

    let scriptTag = document.querySelector('script[data-schema="about"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'about');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="about"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About Us | SmartSite Solutions - South Wales</title>
        <meta name="description" content="Anti-agency web engineering for tradespeople in South Wales. High-speed React sites, local SEO, AI schema and one-touch lead generation. Founded in North Cornelly." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/about" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Section 1: The Hero */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-24"
          >
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold mb-6">
              We Are the <span className="text-gradient-cyan">Anti-Agency.</span>
            </h1>
            <p className="text-lg lg:text-2xl text-muted-foreground">
              No jargon. No false promises. Just high-performance engineering for trades.
            </p>
          </motion.section>

          {/* Section 2: The Origin Story (Side-by-Side) */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 lg:p-12 overflow-hidden">
              {/* Subtle glows */}
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl" />
              
              <div className="relative flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16">
                {/* Left: Circular Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    {/* Glow ring */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/30 to-orange-500/30 blur-xl scale-110" />
                    <div className="relative w-48 h-48 lg:w-56 lg:h-56 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                      <img 
                        src={founderPhoto} 
                        alt="James Stanton - Founder of SmartSite Solutions" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  {/* Name below avatar */}
                  <div className="text-center mt-6">
                    <h3 className="font-display text-2xl font-bold text-foreground">James Stanton</h3>
                    <p className="text-base text-cyan-400 font-medium">Founder</p>
                  </div>
                </div>
                
                {/* Right: The Story */}
                <div className="flex-1 space-y-5">
                  <Quote className="w-10 h-10 text-cyan-400/50" />
                  
                  <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground leading-tight">
                    Bridging the Gap Between Tech & Trade.
                  </h2>
                  
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>
                      I didn't spend the last 20 years hiding behind a screen. I spent them working face-to-face with customers in retail and insurance. I know that business owners don't care about code—they care about communication and cash flow.
                    </p>
                    <p>
                      I founded SmartSite Solutions to bridge the gap. I use the latest AI technology to build high-performance sites, but I run the business with old-school Welsh reliability. No jargon. No ghosting. Just results.
                    </p>
                  </div>
                  
                  {/* Key stats */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:gap-6 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-2xl font-bold text-cyan-400">🤝</p>
                      <p className="text-xs text-muted-foreground">Service Expert</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">90+</p>
                      <p className="text-xs text-muted-foreground">PageSpeed Score</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-400">5★</p>
                      <p className="text-xs text-muted-foreground">Google Reviews</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: The Manifesto */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            {/* Dark Glass Container */}
            <div className="bg-slate-950/50 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 md:p-8 lg:p-12">
              {/* Section Header */}
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
                  The SmartSite Solutions Truth: <span className="text-gradient-cyan">Straight Talk, Real Results.</span>
                </h2>
              </div>

              {/* Manifesto Cards */}
              <div className="space-y-8">
                {manifestoCards.map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 md:p-6 lg:p-8 hover:border-white/20 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/30 flex items-center justify-center mb-4">
                        <card.icon className="w-7 h-7 text-orange-400" />
                      </div>
                      <h3 className="font-display text-xl lg:text-2xl font-bold text-foreground mb-3">
                        {card.title}
                      </h3>
                      <div className="space-y-3 text-slate-300 leading-relaxed">
                        {card.content.map((paragraph, i) => (
                          <p key={i}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Section 4: Local Roots */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 lg:p-12 overflow-hidden text-center">
              {/* Background map visual effect */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 network-grid" />
              </div>
              
              {/* Glows */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
              
              <div className="relative z-10">
                {/* Map Pin Icon */}
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/30 flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-8 h-8 md:w-10 md:h-10 text-orange-400" />
                </div>
                
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
                  Proudly Based in <span className="text-gradient-cyan">North Cornelly.</span>
                </h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Serving the entire M4 corridor from Newport to Llanelli.
                </p>
                
                {/* CTA Button */}
                <PrimaryCTA size="lg" />
              </div>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
